/**
 * Direct-click hotspot hit testing for interactive scenery.
 *
 * Ordinary gameplay activates objects only when the pointer hits these
 * ellipses — never via proximity, activation zones, or nearest-object search.
 *
 * Pinecone toys are an exception to depth-wins: among overlapping generous
 * pinecone hits, the closest visual/world center to the tap wins.
 */

import { depthFromY } from '@/world-prototype/objects/placeObject';
import { gardenTouchHitRadiusWorld, isGardenObject } from '@/world-prototype/interaction/gardenFood';
import { isKickablePinecone } from '@/world-prototype/interaction/playerInteraction';
import type { InteractiveObject, SpawnPoint } from '@/world-prototype/types';

/**
 * True when `point` lies inside the object's circular hotspot
 * (ellipse in screen/world space — radius is world-width units, Y aspect-corrected).
 */
export function pointInObjectHotspot(
  point: SpawnPoint,
  obj: InteractiveObject,
  worldAspect: number,
  hitRadiusOverride?: number,
): boolean {
  const radius = hitRadiusOverride ?? obj.hitRadius;
  if (radius <= 0) return false;
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const dx = point.x - obj.x;
  const dy = (point.y - obj.y) / aspect;
  return dx * dx + dy * dy <= radius * radius;
}

function hotspotDistSq(
  point: SpawnPoint,
  obj: InteractiveObject,
  worldAspect: number,
): number {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const dx = point.x - obj.x;
  const dy = (point.y - obj.y) / aspect;
  return dx * dx + dy * dy;
}

/**
 * Among kickable pinecones under `point` (using each object's hit radius,
 * optionally expanded), pick the closest visual/world center.
 */
export function resolveClosestPineconeAtPoint(
  point: SpawnPoint,
  objects: readonly InteractiveObject[],
  worldAspect: number,
  hitRadiusWorld?: number,
): InteractiveObject | null {
  let best: InteractiveObject | null = null;
  let bestDist = Infinity;

  for (const obj of objects) {
    if (!isKickablePinecone(obj.id)) continue;
    const radius = Math.max(obj.hitRadius, hitRadiusWorld ?? 0);
    if (!pointInObjectHotspot(point, obj, worldAspect, radius)) continue;
    const dist = hotspotDistSq(point, obj, worldAspect);
    if (dist < bestDist) {
      bestDist = dist;
      best = obj;
    }
  }

  return best;
}

/**
 * Frontmost hotspot under `point`, or null for an ordinary ground click.
 * Higher depth (closer to camera / lower on screen) wins overlaps —
 * except pinecones, which resolve by closest center.
 */
export function resolveHotspotAtPoint(
  point: SpawnPoint,
  objects: readonly InteractiveObject[],
  worldAspect: number,
  worldPx?: number,
): InteractiveObject | null {
  const pinecone = resolveClosestPineconeAtPoint(point, objects, worldAspect);
  if (pinecone) return pinecone;

  let best: InteractiveObject | null = null;
  let bestDepth = -Infinity;

  for (const obj of objects) {
    if (isKickablePinecone(obj.id)) continue;
    const radius =
      isGardenObject(obj.id) && worldPx != null
        ? gardenTouchHitRadiusWorld(worldPx, obj.hitRadius)
        : obj.hitRadius;
    if (!pointInObjectHotspot(point, obj, worldAspect, radius)) continue;
    const depth = depthFromY(obj.y);
    if (depth >= bestDepth) {
      bestDepth = depth;
      best = obj;
    }
  }

  return best;
}
